'use client'

import { useCurrentBusinessProfileId } from '@/lib/contexts/BusinessProfileContext'
import { connectGoogleOAuthIntegration } from '@/lib/google-oauth-auth'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { SiGoogleads } from 'react-icons/si'
import { toast } from 'react-toastify'
import Modal from '../Modal/Modal'
import { Spinner } from '../ui/spinner'
import { OAuthButton } from './OAuthButton'

type GoogleAdsCustomer = {
  id: string
  name: string
  isManager: boolean
}

type GoogleAdsOAuthModalProps = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  editMode?: boolean
}

export function GoogleAdsOAuthModal({
  isOpen,
  onClose,
  onSuccess,
  editMode = false,
}: GoogleAdsOAuthModalProps) {
  const [isConnecting, setIsConnecting] = useState(false)
  const [showCustomerSelection, setShowCustomerSelection] = useState(false)
  const [customers, setCustomers] = useState<GoogleAdsCustomer[]>([])
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('')
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [userEmail, setUserEmail] = useState<string>('')
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Get current business profile ID for API calls
  const currentBusinessProfileId = useCurrentBusinessProfileId()

  // Scroll dropdown into view when opened
  useEffect(() => {
    if (isDropdownOpen && dropdownRef.current) {
      setTimeout(() => {
        const dropdownContainer = dropdownRef.current
        const scrollableParent = dropdownContainer?.closest('.overflow-y-auto')

        if (dropdownContainer && scrollableParent) {
          const containerRect = dropdownContainer.getBoundingClientRect()
          const parentRect = scrollableParent.getBoundingClientRect()

          // Dropdown appears 240px above button (max-h-60 = 240px) + margin
          const dropdownHeight = 240 + 8
          const neededSpaceAbove = dropdownHeight + 20

          const currentTop = containerRect.top - parentRect.top
          const targetScroll = scrollableParent.scrollTop + currentTop - neededSpaceAbove

          scrollableParent.scrollTo({
            top: Math.max(0, targetScroll),
            behavior: 'smooth',
          })
        }
      }, 50)
    }
  }, [isDropdownOpen])

  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(!isDropdownOpen)
  }, [isDropdownOpen])

  const handleCustomerSelect = useCallback(
    async (customerId: string) => {
      setSelectedCustomerId(customerId)
      setIsDropdownOpen(false)

      // Auto-save the selected customer
      setIsSaving(true)
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/googleAds?merge=true${currentBusinessProfileId ? `&businessProfileId=${currentBusinessProfileId}` : ''}`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              selectedCustomerId: customerId,
            }),
          }
        )

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error('Failed to save customer selection')
          return
        }

        toast.success('Customer selection saved')
        onSuccess()
      } catch (error) {
        console.error('Error saving customer selection:', error)
        toast.error('Failed to save customer selection')
      } finally {
        setIsSaving(false)
      }
    },
    [onSuccess, currentBusinessProfileId]
  )

  const handleBackdropClick = useCallback(() => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false)
    }
  }, [isDropdownOpen])

  const loadSelectedCustomer = useCallback(async () => {
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/credentials/googleAds${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.credentials?.selectedCustomerId) {
          setSelectedCustomerId(data.credentials.selectedCustomerId)
        }
      }
    } catch (error) {
      console.error('Error loading selected customer:', error)
    }
  }, [currentBusinessProfileId])

  const fetchUserInfo = useCallback(async () => {
    setIsLoadingUser(true)
    try {
      const searchParams = new URLSearchParams()
      if (currentBusinessProfileId) {
        searchParams.set('businessProfileId', currentBusinessProfileId)
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/googleads/user${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
        {
          credentials: 'include',
        }
      )

      // Handle 404 gracefully (no credentials yet)
      if (response.status === 404) {
        console.log('[GoogleAdsOAuthModal] No credentials found yet')
        setUserEmail('')
        return
      }

      // Handle 500 (credentials exist but access token may be expired - this is non-critical)
      if (response.status === 500) {
        console.log(
          '[GoogleAdsOAuthModal] Failed to fetch user info, access token may be expired (non-critical)'
        )
        setUserEmail('')
        return
      }

      const data = await response.json()

      if (response.ok && data.success) {
        setUserEmail(data.user.email)
      }
    } catch (error) {
      console.error('Error fetching Google Ads user info:', error)
      setUserEmail('')
    } finally {
      setIsLoadingUser(false)
    }
  }, [currentBusinessProfileId])

  const fetchCustomers = useCallback(
    async (isEditMode: boolean) => {
      setIsLoadingCustomers(true)
      try {
        const searchParams = new URLSearchParams()
        if (currentBusinessProfileId) {
          searchParams.set('businessProfileId', currentBusinessProfileId)
        }

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/integrations/googleads/customers${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
          {
            credentials: 'include',
          }
        )

        // Handle 404 gracefully (no credentials yet)
        if (response.status === 404) {
          console.log(
            '[GoogleAdsOAuthModal] No credentials found, user needs to authenticate first'
          )
          setCustomers([])
          return
        }

        // Handle 500 (credentials exist but are invalid/expired)
        if (response.status === 500) {
          console.log('[GoogleAdsOAuthModal] Failed to fetch customers, credentials may be invalid')
          toast.error('Failed to load Google Ads customers. Please reconnect your account.', {
            autoClose: 5000,
          })
          setCustomers([])
          return
        }

        const data = await response.json()

        if (!response.ok || !data.success) {
          toast.error(data.error || 'Failed to load Google Ads customers')
          return
        }

        setCustomers(data.customers || [])

        // Only load previously selected customer in edit mode
        if (isEditMode) {
          await loadSelectedCustomer()
        }
        // In connect mode, start with no selection (None)
      } catch (error) {
        console.error('Error fetching Google Ads customers:', error)
        toast.error('Failed to load Google Ads customers')
      } finally {
        setIsLoadingCustomers(false)
      }
    },
    [loadSelectedCustomer, currentBusinessProfileId]
  )

  useEffect(() => {
    if (isOpen) {
      if (editMode) {
        // In edit mode, fetch existing user info and show customer selection
        setShowCustomerSelection(true)
        void fetchUserInfo()
        void fetchCustomers(true)
      } else {
        // Reset state when modal opens in connect mode
        setShowCustomerSelection(false)
        setCustomers([])
        setSelectedCustomerId('')
        setUserEmail('')
      }
    } else {
      // Reset state when modal closes
      setShowCustomerSelection(false)
      setCustomers([])
      setSelectedCustomerId('')
      setUserEmail('')
      setIsDropdownOpen(false)
    }
  }, [isOpen, editMode, fetchUserInfo, fetchCustomers])

  const handleConnect = async () => {
    setIsConnecting(true)
    try {
      const success = await connectGoogleOAuthIntegration()

      if (!success) {
        toast.error('Failed to connect to Google Ads')
        return
      }

      // OAuth successful, now fetch user info and customers
      setShowCustomerSelection(true)
      await fetchUserInfo()
      await fetchCustomers(false) // Connect mode - start with no selection
    } catch (error) {
      console.error('Error connecting to Google Ads:', error)
      toast.error('Failed to connect to Google Ads')
    } finally {
      setIsConnecting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="font-hanken-grotesk flex w-full max-w-xl flex-col p-8">
        <div className="w-full rounded-3xl bg-white p-8 text-black shadow-xl">
          {!showCustomerSelection ? (
            <>
              <h2 className="mb-2 text-2xl font-semibold text-black">Connect to Google Ads</h2>
              <p className="mb-6 text-base text-black/70">
                Connect your Google Ads account to access campaign performance and keyword data
              </p>

              <div className="space-y-4">
                <OAuthButton
                  onClick={handleConnect}
                  disabled={isConnecting}
                  isLoading={isConnecting}
                  icon={<SiGoogleads className="h-5 w-5" />}
                >
                  {isConnecting ? 'Connecting...' : 'Connect with Google Ads'}
                </OAuthButton>
              </div>

              <div className="mt-4 flex justify-center">
                <button
                  onClick={onClose}
                  className="text-sm text-black/70 transition-colors hover:text-black"
                  disabled={isConnecting}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <h2 className="mb-2 text-2xl font-semibold text-black">
                {editMode ? 'Edit Google Ads Connection' : 'Select a Google Ads Account'}
              </h2>
              <p className="mb-6 text-base text-black/70">
                {editMode
                  ? 'Update your Google Ads account selection'
                  : 'Choose the Google Ads account you want to use'}
              </p>

              {isLoadingUser || isLoadingCustomers ? (
                <div className="flex min-h-[200px] items-center justify-center">
                  <Spinner size={32} />
                </div>
              ) : (
                <>
                  {userEmail && (
                    <div className="mb-6 flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <SiGoogleads className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-600">Connected as</p>
                        <p className="truncate text-sm font-semibold text-black">{userEmail}</p>
                      </div>
                    </div>
                  )}

                  {/* Customer Selection */}
                  <div>
                    <label className="mb-1 block text-sm font-medium text-black">
                      Google Ads Account
                    </label>
                    <p className="mb-2 text-sm text-black/60">
                      Select the account you want to use for your ads
                    </p>

                    <div className="relative" ref={dropdownRef}>
                      {/* Backdrop */}
                      <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="fixed inset-0 bg-transparent"
                            style={{ zIndex: 60 }}
                            onClick={handleBackdropClick}
                          />
                        )}
                      </AnimatePresence>
                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -8, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -8, scale: 0.96 }}
                            transition={{
                              duration: 0.2,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                            className="absolute bottom-full left-0 z-[70] mb-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
                            style={{
                              transformOrigin: 'bottom left',
                              backdropFilter: 'blur(8px)',
                            }}
                          >
                            <div className="max-h-60 overflow-y-auto p-2">
                              <div className="space-y-1">
                                {customers
                                  .filter((customer) => !customer.isManager)
                                  .map((customer) => {
                                    const isSelected = customer.id === selectedCustomerId

                                    return (
                                      <motion.button
                                        key={customer.id}
                                        onClick={() => handleCustomerSelect(customer.id)}
                                        whileHover={
                                          !isSelected
                                            ? { backgroundColor: 'rgb(249 250 251)' }
                                            : undefined
                                        }
                                        whileTap={!isSelected ? { scale: 0.98 } : undefined}
                                        className={cn(
                                          'flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors',
                                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1',
                                          {
                                            'bg-blue-50 text-blue-700': isSelected,
                                            'text-gray-700 hover:bg-gray-50': !isSelected,
                                            'cursor-not-allowed': isSelected,
                                          }
                                        )}
                                      >
                                        <div className="flex-shrink-0">
                                          <SiGoogleads
                                            className={cn('h-4 w-4', {
                                              'text-blue-500': isSelected,
                                              'text-gray-400': !isSelected,
                                            })}
                                          />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <div className="flex items-center gap-2">
                                            <span className="truncate font-medium">
                                              {customer.name}
                                            </span>
                                            {isSelected && (
                                              <div className="h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                                            )}
                                          </div>
                                          <span className="text-xs text-gray-500">
                                            {customer.id}
                                          </span>
                                        </div>
                                      </motion.button>
                                    )
                                  })}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Trigger Button */}
                      <motion.button
                        onClick={toggleDropdown}
                        disabled={isSaving}
                        whileHover={!isSaving ? { backgroundColor: 'rgb(243 243 243)' } : undefined}
                        whileTap={!isSaving ? { scale: 0.98 } : undefined}
                        className={cn(
                          'flex w-full items-center gap-3 rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 text-base transition-colors',
                          'focus-visible:border-[#B0B0B0] focus-visible:outline-none',
                          {
                            'cursor-not-allowed opacity-50': isSaving,
                          }
                        )}
                      >
                        <span
                          className={cn('line-clamp-1 min-w-0 flex-1 text-left', {
                            'text-gray-400': !selectedCustomerId,
                            'text-black': selectedCustomerId,
                          })}
                        >
                          {selectedCustomerId
                            ? customers.find((c) => c.id === selectedCustomerId)?.name
                            : 'None'}
                        </span>
                        <motion.div
                          animate={{ rotate: isDropdownOpen ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="flex-shrink-0 text-gray-400"
                        >
                          <ChevronDown className="h-5 w-5" />
                        </motion.div>
                      </motion.button>
                    </div>
                  </div>

                  <div className="mt-4 flex justify-center">
                    <button
                      onClick={onClose}
                      className="text-sm text-black/70 transition-colors hover:text-black"
                      disabled={isSaving}
                    >
                      Close
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
